import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal } from '@angular/core';
import { awarenessGoals, considerationGoals, conversionGoals } from '../../../../common/app-config/layout';
import { CampaignDto } from '../../../../common/general-types/campaigns.dto';
import { UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { ProductService } from '../../../../services/product/product.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CampaignService } from '../../../../services/campaign/campaign.service';
import { ImageService } from '../../../../services/image/image.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { debounceTime, Subject, Subscription, take } from 'rxjs';
import { FormField, form } from '@angular/forms/signals';
import { TooltipDirective } from '../../../../common/directives/tooltip/tooltip.directive';
import { DeliveryTypeEnum } from '../../../../common/general-types/briefs.dto';
import { CreateCampaignComponent } from '../../../../molecules/create-campaign/create-campaign.component';
import { BrandProductsCreateComponent } from '../../../my-brand/molecules/brand-products-create/brand-products-create.component';

@Component({
  selector: 'agent-step-basic-info',
  standalone: true,
  imports: [FormField, TooltipDirective, CreateCampaignComponent, BrandProductsCreateComponent],
  templateUrl: './basic-info.component.html',
  styleUrl: './basic-info.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BasicInfoComponent implements OnInit {

  DeliveryTypeEnum = DeliveryTypeEnum;

  awarenessGoals: { id: number, name: string, description: string, color: string, icon: string }[] = awarenessGoals;
  considerationGoals: { id: number, name: string, description: string, color: string, icon: string }[] = considerationGoals;
  conversionGoals: { id: number, name: string, description: string, color: string, icon: string }[] = conversionGoals;

  /** All Product type options */
  allProductTypes: { id: number, name: string }[] = [
    { id: 0, name: '🏷 Physical product' },
    { id: 1, name: '🧑‍💻 Digital service' },
    { id: 2, name: '📱 App' },
    { id: 3, name: '🎟 Event/Tickets' },
  ];

  /** Produt Delivery Types */
  deliveryOptions: { colour: string, icon: string, title: string, desc: string, id: DeliveryTypeEnum }[] = [
    { colour: 'bg-yellow-500/10 text-yellow-400 rounded-l-md border-t border-l border-b border-yellow-400', icon: '', title: "No", desc: "Not sending", id: DeliveryTypeEnum.none },
    { colour: 'bg-pink-600/10 text-pink-400 rounded-l-md border-t border-l border-b border-pink-400', icon: '', title: "Discount", desc: "Will send a coupon", id: DeliveryTypeEnum.Discount_Code },
    { colour: 'bg-green-600/10 text-green-400 rounded-l-md border-t border-l border-b border-green-400', icon: '', title: "Gifting", desc: "Will send the product", id: DeliveryTypeEnum.Address_Required },
  ]

  allCampaigns = signal<CampaignDto[] | undefined>(undefined);
  showCreateCampaignModal = signal<boolean>(false);
  allProducts = signal<any | undefined>(undefined);
  displayProducts = signal<any | undefined>(undefined);

  showCreateProductModal = signal<boolean>(false);

  private subscriptions = new Subscription();
  private productSearchQuerySubject = new Subject<string>();
  productSearchQueryModel = signal({
    query: '',
  })
  productSearchQueryForm = form(this.productSearchQueryModel)

  /** Services */
  public imageService = inject(ImageService);
  private currentUserService = inject(CurrentUserService);
  private campaignService = inject(CampaignService);
  private productService = inject(ProductService);
  public createBriefService = inject(CreateBriefService);
  private sanitizer = inject(DomSanitizer);
  /** Services - END */

  /** Sentinel for `el-select` when no campaign; must match the No Campaign `el-option` value. */
  readonly campaignSelectNoneValue = '__none__';

  readonly campaignSelectValue = computed(() => {
    const selected = this.createBriefService.selectedCampaign();
    return selected?.id ?? this.campaignSelectNoneValue;
  });

  constructor() {
    this.subscriptions.add(
      this.productSearchQuerySubject.pipe(debounceTime(150)).subscribe(query => {
        console.log('query changed:', query);
        this.filterAllProducts(query);
      })
    );

    effect(() => {
      const query = this.productSearchQueryModel().query;
      this.productSearchQuerySubject.next(query);
    });
  }

  ngOnInit(): void {
    this.getAllCampaigns();
    this.getBusinessProducts();
  }

  getAllCampaigns() {
    console.log('getAllCampaigns(): ', this.currentUserService.focussedBrand.id);
    this.campaignService.getAllBusinessCampaigns(this.currentUserService.focussedBrand.id).pipe(take(1)).subscribe((response: any) => {
      this.allCampaigns.set(response);
    });
  }

  getBusinessProducts() {
    this.productService.getbusinessProducts(this.currentUserService.focussedBrand.id).pipe(take(1)).subscribe((response) => {
      console.log('Products:', response);
      this.allProducts.set(response);
      this.displayProducts.set(response);
    });
  }

  filterAllProducts(query: string) {
    if (query.length > 0) {
      this.displayProducts.set(this.allProducts()?.filter((product: any) => product.productName.toLowerCase().includes(query.toLowerCase())));
    } else {
      this.displayProducts.set(this.allProducts());
    }
  }

  onProductSelect(product: any) {
    this.createBriefService.selectedProduct.set(product);
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onAddCampaignClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showCreateCampaignModal.set(true);
  }

  onCampaignCreated(campaign: CampaignDto): void {
    this.showCreateCampaignModal.set(false);
    this.campaignService.getAllBusinessCampaigns(this.currentUserService.focussedBrand.id).pipe(take(1)).subscribe((response: unknown) => {
      const list = response as CampaignDto[];
      this.allCampaigns.set(list);
      const match = list.find((c) => c.id === campaign.id);
      this.createBriefService.selectedCampaign.set(match ?? campaign);
    });
  }

  onProductAdded(newProduct: UgcProductResponseDto): void {
    this.showCreateProductModal.set(false);
    const brandId = this.currentUserService.focussedBrand.id;
    this.productService.getbusinessProducts(brandId).pipe(take(1)).subscribe((response) => {
      const list = response as UgcProductResponseDto[];
      this.allProducts.set(list);
      this.filterAllProducts(this.productSearchQueryModel().query);
      const match = list.find((p) => p.id === newProduct.id);
      this.createBriefService.selectedProduct.set(match ?? newProduct);
    });
  }
}
